import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Alert,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * ModulePermissionsTable Component
 * Displays all modules with their permissions in a simple table format
 * Modules on the left, permissions (View, Edit, Create, Delete) as column headers
 */
const ModulePermissionsTable = ({
  modules = [],
  permissions = {},
  onChange,
  loading = false,
  disabled = false,
  mode = 'role',
  overrides = {},
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter modules based on search
  const filteredModules = modules.filter(
    (module) =>
      module.ModuleName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      module.Description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handlePermissionChange = (moduleName, permissionType, value) => {
    const modulePerms = permissions[moduleName] || {
      CanView: false,
      CanEdit: false,
      CanCreate: false,
      CanDelete: false,
    };

    onChange({
      ...permissions,
      [moduleName]: {
        ...modulePerms,
        [permissionType]: value,
      },
    });
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {t('access_management.loading_modules')}...
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder={t('access_management.search_modules')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* User Override Notice */}
      {mode === 'user' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('access_management.user_override_notice')}
        </Alert>
      )}

      {/* No Results */}
      {filteredModules.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {searchQuery 
              ? t('access_management.no_modules_found')
              : t('access_management.no_modules_available')
            }
          </Typography>
        </Paper>
      )}

      {/* Permissions Table */}
      {filteredModules.length > 0 && (
        <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#667eea' }}>
                <TableCell 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '1rem',
                    minWidth: 200,
                  }}
                >
                  {t('access_management.module')}
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '0.95rem',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <ViewIcon fontSize="small" />
                    {t('access_management.view')}
                  </Box>
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '0.95rem',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <EditIcon fontSize="small" />
                    {t('access_management.edit')}
                  </Box>
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '0.95rem',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <AddIcon fontSize="small" />
                    {t('access_management.create')}
                  </Box>
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '0.95rem',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <DeleteIcon fontSize="small" />
                    {t('access_management.delete')}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredModules.map((module, index) => {
                const moduleName = module.ModuleName;
                const modulePerms = permissions[moduleName] || {
                  CanView: false,
                  CanEdit: false,
                  CanCreate: false,
                  CanDelete: false,
                };

                return (
                  <TableRow
                    key={module.Id || module.id}
                    sx={{
                      '&:nth-of-type(odd)': {
                        bgcolor: '#f9f9f9',
                      },
                      '&:hover': {
                        bgcolor: '#f0f0f0',
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body1" fontWeight={600}>
                        {module.ModuleName}
                      </Typography>
                      {module.Description && (
                        <Typography variant="caption" color="text.secondary">
                          {module.Description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={modulePerms.CanView || false}
                        onChange={(e) =>
                          handlePermissionChange(
                            moduleName,
                            'CanView',
                            e.target.checked
                          )
                        }
                        disabled={disabled}
                        sx={{
                          color: '#2196f3',
                          '&.Mui-checked': {
                            color: '#2196f3',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={modulePerms.CanEdit || false}
                        onChange={(e) =>
                          handlePermissionChange(
                            moduleName,
                            'CanEdit',
                            e.target.checked
                          )
                        }
                        disabled={disabled}
                        sx={{
                          color: '#ff9800',
                          '&.Mui-checked': {
                            color: '#ff9800',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={modulePerms.CanCreate || false}
                        onChange={(e) =>
                          handlePermissionChange(
                            moduleName,
                            'CanCreate',
                            e.target.checked
                          )
                        }
                        disabled={disabled}
                        sx={{
                          color: '#4caf50',
                          '&.Mui-checked': {
                            color: '#4caf50',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={modulePerms.CanDelete || false}
                        onChange={(e) =>
                          handlePermissionChange(
                            moduleName,
                            'CanDelete',
                            e.target.checked
                          )
                        }
                        disabled={disabled}
                        sx={{
                          color: '#f44336',
                          '&.Mui-checked': {
                            color: '#f44336',
                          },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ModulePermissionsTable;
